import React, { useEffect, useState } from 'react';
import { App, Button, Checkbox, Modal, Space, Typography } from 'antd';
import {
  acceptYanxuanMonthlyTripartiteAgreement,
  fetchYanxuanMonthlyTripartiteAgreement,
  type YanxuanMonthlyTripartiteAgreementStatus,
} from '../../services/yanxuan-agreement.service';

type Props = {
  open: boolean;
  /** 供应商工作台：拒绝则退出；采购端：拒绝则退回模式选择 */
  onAccepted: () => void;
  onCancel: () => void;
  cancelText?: string;
};

/**
 * 严选月结三方协议首次阅读同意弹窗。
 * 对齐现网 LegalConsentModal：不可点遮罩关闭、须勾选后确认。
 */
const YanxuanMonthlyAgreementModal: React.FC<Props> = ({
  open,
  onAccepted,
  onCancel,
  cancelText = '暂不同意',
}) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);
  const [doc, setDoc] = useState<YanxuanMonthlyTripartiteAgreementStatus | null>(null);

  useEffect(() => {
    if (!open) return;
    setChecked(false);
    let active = true;
    setLoading(true);
    fetchYanxuanMonthlyTripartiteAgreement()
      .then((data) => {
        if (!active) return;
        setDoc(data);
        if (data.accepted) {
          onAccepted();
        }
      })
      .catch((e: any) => {
        if (!active) return;
        message.error(e?.response?.data?.message || '加载协议失败');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, onAccepted]);

  const handleAccept = async () => {
    if (!checked) {
      message.warning('请先勾选「已阅读并同意」');
      return;
    }
    try {
      setSubmitting(true);
      await acceptYanxuanMonthlyTripartiteAgreement();
      message.success('协议确认成功');
      onAccepted();
    } catch (e: any) {
      message.error(e?.response?.data?.message || '协议确认失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={doc?.title || '联采严选月结三方协议'}
      open={open}
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      width={720}
      zIndex={1600}
      footer={
        <Space>
          <Button onClick={onCancel}>{cancelText}</Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={!checked || loading || !doc}
            onClick={() => void handleAccept()}
          >
            同意并继续
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        协议主体为采购方、严选供应商与平台。请完整阅读后勾选确认；同意后将按组织/供应商持久化，不再反复弹出。
        {doc?.version ? `（版本 ${doc.version}）` : null}
      </Typography.Paragraph>
      <div
        style={{
          maxHeight: 360,
          overflow: 'auto',
          padding: '12px 14px',
          border: '1px solid var(--ant-color-border, #d9d9d9)',
          borderRadius: 8,
          background: 'var(--ant-color-fill-alter, #fafafa)',
          whiteSpace: 'pre-wrap',
          lineHeight: 1.65,
          fontSize: 13,
          marginBottom: 16,
        }}
      >
        {loading ? '协议加载中…' : doc?.content || '暂无协议正文'}
      </div>
      <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)}>
        我已阅读并同意《{doc?.title || '联采严选月结三方协议'}》
      </Checkbox>
    </Modal>
  );
};

export default YanxuanMonthlyAgreementModal;
